import { useQuery } from '@tanstack/react-query';

import type { AtsCredential, Connection } from 'clients/ATSIntegrationsClient/types';

import { atsIntegrationsClient } from 'clients/ATSIntegrationsClient/ATSIntegrationsClient';
import { QueryKey } from 'types/query';
import { useCurrentUserQuery } from './useCurrentUserQuery';

type AtsConnection = Connection & { is_active: boolean; shouldConfirmChange: boolean };

export const useGetAllAtsConnectionsQuery = () => {
  const { data: currentUser } = useCurrentUserQuery();

  return useQuery(
    [QueryKey.useGetAllAtsConnectionsQuery],
    async () => {
      const { data: { my_connections = [], team_connections = [] } = {} } = await atsIntegrationsClient.getAll();

      const activeATS: AtsCredential | undefined = [...team_connections, ...my_connections].find(
        ({ is_active }) => is_active === true,
      );

      const myConnections: AtsConnection[] = my_connections.map((connection) => ({
        ...connection,
        is_active: true,
        shouldConfirmChange: false,
      }));

      const teamConnections: AtsConnection[] = team_connections
        .map((connection) => ({
          ...connection,
          is_active: false,
          shouldConfirmChange: true,
        }))
        .toSorted(
          (a, b) =>
            (b.connected_users.length - a.connected_users.length) * 5 +
            (a.ats_name?.localeCompare(b.ats_name ?? '') ?? 0) * 3 +
            (a.name?.localeCompare(b.name ?? '') ?? 0) * 2 +
            (a.id - b.id),
        );

      return { activeATS, myConnections, teamConnections } as const;
    },
    {
      enabled: !!currentUser,
      retry: false,
      initialData: { activeATS: undefined, myConnections: [], teamConnections: [] },
      initialDataUpdatedAt: 0,
    },
  );
};
